import * as React from 'react';

import {
  createStyles,
  Grid,
  Theme,
  WithStyles,
  withStyles
} from '@material-ui/core';

import { ILieuucationPropertiesMarketProfile } from '@lieuu/core';

import BarChart from '../charts/BarChart';
import LineYearlyChart from '../charts/LineYearlyChart';
import PieChart from '../charts/PieChart';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(2),
      [theme.breakpoints.up('lg')]: {
        paddingLeft: theme.spacing(6),
        paddingRight: theme.spacing(6)
      }
    },
    grid: { width: '100%' },
    divider: {
      height: '100%'
    }
  });

type IProps = WithStyles<typeof styles> & {
  properties: ILieuucationPropertiesMarketProfile;
  cbsaProperties: ILieuucationPropertiesMarketProfile;
};

const MarketProfile: React.FC<IProps> = ({
  classes,
  properties,
  cbsaProperties
}) => {
  const stemDescription = (
    <>
      {
        'This is the college degree field for neighborhood residents who completed a degree.'
      }
      <i>{' This does not include non-graduates.'}</i>
      {' The list of STEM degrees as defined by the NSF is: '}
      <br />
      <b>
        {
          'Computers, Mathematics and Statistics; Biological, Agricultural, and Environmental Sciences; Physical and Related Sciences; Psychology; Social Sciences; Engineering; and Multidisciplinary Studies.'
        }
      </b>
    </>
  );

  return (
    <div className={classes.root}>
      <Grid
        alignItems="center"
        justify="center"
        spacing={2}
        className={classes.grid}
        container
      >
        <LineYearlyChart
          title="Population"
          variant="plain"
          trendLieuucation={properties.population.trend}
          trendCbsa={cbsaProperties.population.trend}
          description={
            <>
              {
                'This is defined as the amount of residents in the neighborhood.'
              }
            </>
          }
        />

        <LineYearlyChart
          title="Age of Residents"
          variant="plain"
          trendLieuucation={properties.typicalAge.trend}
          trendCbsa={cbsaProperties.typicalAge.trend}
          description={
            <>
              {
                'This is defined as the median age of residents in the neighborhood.'
              }
            </>
          }
        />

        <BarChart
          title="Degree Field"
          data={[
            {
              id: 'Arts',
              lieuucation: properties.degreeField.artsAndHumanities,
              cbsa: cbsaProperties.degreeField.artsAndHumanities
            },
            {
              id: 'Business',
              lieuucation: properties.degreeField.business,
              cbsa: cbsaProperties.degreeField.business
            },
            {
              id: 'Education',
              lieuucation: properties.degreeField.education,
              cbsa: cbsaProperties.degreeField.education
            },
            {
              id: 'STEM',
              lieuucation: properties.degreeField.stem,
              cbsa: cbsaProperties.degreeField.stem
            }
          ]}
          description={stemDescription}
        />

        <BarChart
          title="Bachelor's or Higher"
          variant="plain-percent"
          data={[
            {
              id: 'College Degree',
              lieuucation: properties.bachelorsOrHigher,
              cbsa: cbsaProperties.bachelorsOrHigher
            }
          ]}
          description={
            <>
              {
                "This is the percent of inhabitants who have attained a Bachelor's Degree or higher education from an accredited college."
              }
              <b>
                {' Some coursework completed towards a degree is not included.'}
              </b>
            </>
          }
        />

        {/* 
        <PieChart
          title="Sex Distribution"
          data={[
            {
              id: 'Female',
              value: properties.female
            },
            {
              id: 'Male',
              value: properties.male
            }
          ]}
          description={
            <>
              {
                'This is the reported biological sex for residents of the neighborhood. This'
              }
              <i>{' does not '}</i>
              {
                ' take into account gender identity or any other sexual identities.'
              }
            </>
          }
        /> */}

        <LineYearlyChart
          title="Household Income Trend"
          trendLieuucation={properties.income.household.trend}
          trendCbsa={cbsaProperties.income.household.trend}
          description={
            <>
              {'This is defined as the median in the neighborhood of'}
              <b>{' income during the past 12 months'}</b>
              {
                ' of the householder and all other individuals 15 years old and over in the household, whether they are related to the householder or not.'
              }
            </>
          }
          wide
        />

        <PieChart
          title="Mode of Transportation"
          data={[
            {
              id: 'Car',
              value: properties.workTransport.car
            },
            {
              id: 'Public Transportation',
              value: properties.workTransport.public.overall
            },
            {
              id: 'Motorcycle',
              value: properties.workTransport.motorcycle
            },
            {
              id: 'Taxi',
              value: properties.workTransport.taxi
            },
            {
              id: 'Walk or Bike',
              value:
                properties.workTransport.walk + properties.workTransport.bike
            },
            {
              id: 'Work from Home',
              value: properties.workTransport.workAtHome
            }
          ]}
          description={
            <>
              {'This is a breakdown of'}
              <b>{' the methods of transport people use to get to work.'}</b>
              {
                ' Working from home is considered its own category since it does not require transport to work.'
              }
            </>
          }
        />

        <PieChart
          title="Public Transportation Choices"
          data={[
            {
              id: 'Bus',
              value: properties.workTransport.public.bus
            },
            {
              id: 'Subway',
              value: properties.workTransport.public.subway
            },
            {
              id: 'Other',
              value: properties.workTransport.public.other
            }
          ]}
          description={
            <>
              {'This is a detailed breakdown of'}
              <b>
                {' the public transportation choices that inhabitants use.'}
              </b>
            </>
          }
        />

        <BarChart
          title="Technology Adoption"
          variant="plain-percent"
          data={[
            {
              id: 'Owns a Computer',
              lieuucation: properties.homeComputers,
              cbsa: cbsaProperties.homeComputers
            },
            {
              id: 'Owns a Smartphone',
              lieuucation: properties.smartphone,
              cbsa: cbsaProperties.smartphone
            }
          ]}
          description={
            <>
              {
                'This is the percent household ownership of a desktop or laptop and a smartphone.'
              }
              <b>
                {
                  ' Typically, technology ownership is used to indicate technology adoption in neighborhoods.'
                }
              </b>
            </>
          }
        />
      </Grid>
    </div>
  );
};

export default withStyles(styles)(MarketProfile);
